"use client";

import { useEffect, useRef, useState } from "react";

interface Stat {
  value: number;
  suffix: string;
  labelEn: string;
  labelZh: string;
}

const STATS: Stat[] = [
  { value: 500, suffix: "+", labelEn: "Projects Completed", labelZh: "完工項目" },
  { value: 15, suffix: "+", labelEn: "Years Experience", labelZh: "年行業經驗" },
  { value: 98, suffix: "%", labelEn: "Client Satisfaction", labelZh: "客戶滿意率" },
  { value: 50, suffix: "+", labelEn: "Design Awards", labelZh: "設計獎項" },
];

function useCountUp(target: number, duration: number, active: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = 0;
    const increment = target / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [active, target, duration]);
  return count;
}

function StatItem({ stat, active }: { stat: Stat; active: boolean }) {
  const count = useCountUp(stat.value, 2000, active);
  return (
    <div className="flex flex-col items-center text-center p-6">
      <div className="font-display text-5xl md:text-6xl font-bold shimmer mb-2">
        {count}{stat.suffix}
      </div>
      <div className="font-bold text-white text-sm">{stat.labelZh}</div>
      <div className="text-xs mt-1" style={{ color: "rgba(245,240,232,0.5)" }}>{stat.labelEn}</div>
    </div>
  );
}

export default function StatsSection() {
  const ref = useRef<HTMLElement>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setActive(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="py-20 px-6 md:px-12 relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #1A2F5E 0%, #0A1628 50%, #1A2F5E 100%)" }}
    >
      {/* Background decorative circles */}
      <div
        className="absolute -top-32 -left-32 w-96 h-96 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(212,175,55,0.08) 0%, transparent 70%)" }}
      />
      <div
        className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(212,175,55,0.06) 0%, transparent 70%)" }}
      />

      {/* Gold line top */}
      <div className="gold-line w-full mb-12 opacity-30" />

      <div className="max-w-screen-xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-0 md:divide-x" style={{ borderColor: "rgba(212,175,55,0.15)" }}>
          {STATS.map((stat) => (
            <StatItem key={stat.labelEn} stat={stat} active={active} />
          ))}
        </div>
      </div>

      {/* Gold line bottom */}
      <div className="gold-line w-full mt-12 opacity-30" />
    </section>
  );
}